"use client";

import Image from "next/image";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { useRef, type ReactNode } from "react";
import { HeroCtaButton } from "@/components/ui/hero-cta-button";
import { HERO_GRADIENT_TEXT, SECTION_TITLE_CLASS } from "@/lib/light-pages";

const SEQUENCE_EASE = [0.16, 1, 0.3, 1] as const;
const CAROUSEL_PERSPECTIVE = "1500px";

const PROJECT_CARD_BASE =
  "flex h-full w-full flex-col gap-5 rounded-2xl border border-zinc-200 bg-white p-5 shadow-[0_16px_40px_-20px_rgba(15,23,42,0.18)] sm:gap-6 sm:rounded-3xl sm:p-6";

const PROJECT_CARD_FEATURED =
  `${PROJECT_CARD_BASE} border-emerald-200 shadow-[0_24px_56px_-18px_rgba(16,185,129,0.38)] ring-1 ring-emerald-100`;

const PROJECT_THUMB_FRAME =
  "relative block w-full shrink-0 overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-50 aspect-video";

type CarouselSlot = "left" | "center" | "right";

const CAROUSEL_POSES: Record<
  CarouselSlot,
  {
    rotateY: number;
    x: string;
    y: number;
    z: number;
    scale: number;
    opacity: number;
    blur: string;
    zIndex: number;
  }
> = {
  left: {
    rotateY: -28,
    x: "-74%",
    y: 36,
    z: -180,
    scale: 0.76,
    opacity: 0.62,
    blur: "2px",
    zIndex: 10,
  },
  center: {
    rotateY: 0,
    x: "0%",
    y: 0,
    z: 80,
    scale: 1,
    opacity: 1,
    blur: "0px",
    zIndex: 30,
  },
  right: {
    rotateY: 28,
    x: "74%",
    y: 36,
    z: -180,
    scale: 0.76,
    opacity: 0.62,
    blur: "2px",
    zIndex: 10,
  },
};

function entranceTransition(
  reducedMotion: boolean | null,
  delay = 0,
  duration = 0.55,
) {
  if (reducedMotion) return { duration: 0 };
  return { delay, duration, ease: SEQUENCE_EASE };
}

function BoostProjectCard({
  featured = false,
  isInView,
  reducedMotion,
  animateCta = false,
}: {
  featured?: boolean;
  isInView: boolean;
  reducedMotion: boolean | null;
  animateCta?: boolean;
}) {
  return (
    <article className={featured ? PROJECT_CARD_FEATURED : PROJECT_CARD_BASE}>
      <div className={PROJECT_THUMB_FRAME}>
        <Image
          src="/futursiteici.png"
          alt="Votre futur site ici"
          width={1600}
          height={900}
          className="block h-full w-full object-cover"
          quality={75}
          sizes="(max-width: 768px) 92vw, 22rem"
          loading="lazy"
        />
      </div>

      <h3 className="shrink-0 text-center text-xl font-bold text-[#1C1C1C] sm:text-2xl">
        Votre site pourrait être le prochain.
      </h3>

      <div className="flex flex-1 flex-col justify-between gap-4">
        <p className="text-center text-sm leading-relaxed text-zinc-600 sm:text-base">
          On aime autant vos résultats que votre image. Rejoignez les projets qui cartonnent.
        </p>

        {animateCta ? (
          <motion.div
            className="flex justify-center [&>a]:justify-center"
            initial={false}
            animate={
              isInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 12, scale: 0.96 }
            }
            transition={entranceTransition(reducedMotion, 0.28, 0.55)}
          >
            <HeroCtaButton>Réserver un appel gratuit</HeroCtaButton>
          </motion.div>
        ) : (
          <div className="flex justify-center [&>a]:justify-center">
            <HeroCtaButton>Réserver un appel gratuit</HeroCtaButton>
          </div>
        )}
      </div>
    </article>
  );
}

function CarouselCard({
  slot,
  isInView,
  reducedMotion,
  delay,
  children,
}: {
  slot: CarouselSlot;
  isInView: boolean;
  reducedMotion: boolean | null;
  delay: number;
  children: ReactNode;
}) {
  const pose = CAROUSEL_POSES[slot];
  const isSide = slot !== "center";
  const hidden = {
    rotateY: 0,
    x: "0%",
    y: 40,
    z: -240,
    scale: 0.68,
    opacity: 0,
    filter: "blur(10px)",
  };
  const visible = {
    rotateY: pose.rotateY,
    x: pose.x,
    y: pose.y,
    z: pose.z,
    scale: pose.scale,
    opacity: pose.opacity,
    filter: `blur(${pose.blur})`,
  };

  return (
    <motion.div
      className={`absolute inset-0 [backface-visibility:hidden] ${isSide ? "pointer-events-none" : ""}`}
      aria-hidden={isSide}
      style={{
        transformStyle: "preserve-3d",
        transformOrigin: "50% 50%",
        zIndex: pose.zIndex,
      }}
      initial={false}
      animate={isInView ? visible : hidden}
      transition={entranceTransition(reducedMotion, delay, 0.72)}
    >
      {children}
    </motion.div>
  );
}

export function ProjetsBoostSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });
  const reducedMotion = useReducedMotion();

  return (
    <section
      ref={sectionRef}
      data-scroll-center
      className="overflow-visible bg-[#f3f3f3] px-6 py-14 sm:py-16"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-10 overflow-visible sm:gap-12">
        <motion.div
          className="mx-auto flex w-full max-w-3xl flex-col items-center text-center"
          initial={false}
          animate={
            isInView
              ? { opacity: 1, y: 0, filter: "blur(0px)" }
              : { opacity: 0, y: 14, filter: "blur(12px)" }
          }
          transition={entranceTransition(reducedMotion, 0, 0.62)}
        >
          <h2 className={`${SECTION_TITLE_CLASS} text-[#1C1C1C]`}>
            Besoin d&apos;un <span className={HERO_GRADIENT_TEXT}>boost</span> de{" "}
            <span className={HERO_GRADIENT_TEXT}>visibilité</span> ?
          </h2>
        </motion.div>

        <div className="relative mx-auto w-full max-w-4xl overflow-visible px-6 sm:px-10 md:px-14">
          <div
            className="relative mx-auto h-[31rem] w-full max-w-[22rem] sm:h-[32rem] sm:max-w-md"
            style={{
              perspective: CAROUSEL_PERSPECTIVE,
              perspectiveOrigin: "50% 38%",
            }}
          >
            <div
              className="relative h-full w-full"
              style={{ transformStyle: "preserve-3d" }}
            >
              <CarouselCard slot="left" isInView={isInView} reducedMotion={reducedMotion} delay={0.1}>
                <BoostProjectCard isInView={isInView} reducedMotion={reducedMotion} />
              </CarouselCard>

              <CarouselCard slot="center" isInView={isInView} reducedMotion={reducedMotion} delay={0.16}>
                <BoostProjectCard
                  featured
                  isInView={isInView}
                  reducedMotion={reducedMotion}
                  animateCta
                />
              </CarouselCard>

              <CarouselCard slot="right" isInView={isInView} reducedMotion={reducedMotion} delay={0.1}>
                <BoostProjectCard isInView={isInView} reducedMotion={reducedMotion} />
              </CarouselCard>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
